import React, { useContext } from "react";
import { getGelTokens } from '@tal-gel/theming';
import { AlertDetail, AlertTypeEnum } from "../../common/models/contentful/dashboard";
import { UserContext } from "../../common/usercontext/user.context";
import { CLAIM_TYPE } from "../../constants/constants";
import {
  GelScreenDetectorContext,
  GelParagraph,
  GelBoxLayout,
  GelIcon
} from '@tal-gel/components';

interface ContentfulAlertProps {
  alerts?: AlertDetail[];
}

export const ContentfulAlert = ({ alerts }: ContentfulAlertProps) => {
  const { contextData } = useContext(UserContext);
  const hasIpClaim = contextData?.claims?.some(claim => claim.claimTypeName === CLAIM_TYPE.IP);
  const hasTpdClaim = contextData?.claims?.some(claim => claim.claimTypeName === CLAIM_TYPE.TPD);
  const hasTiClaim = contextData?.claims?.some(claim => claim.claimTypeName === CLAIM_TYPE.TI);

  return (
    <div data-testid="alert-container">
      {alerts?.map((alert, i) => (
        <AlertDetailComponent 
            key={i} 
            alert={alert} 
            hasIpClaim={hasIpClaim} 
            hasTpdClaim={hasTpdClaim}
            hasTiClaim={hasTiClaim}/>
      ))}
    </div>
  );
};

interface AlertDetailProps {
  alert: AlertDetail;
  hasIpClaim: boolean;
  hasTpdClaim: boolean;
  hasTiClaim: boolean;
}

const AlertDetailComponent = ({ alert, hasIpClaim, hasTpdClaim, hasTiClaim }: AlertDetailProps) => {
  const {
    global: {
      sizeBaseX10, sizeBaseX3, sizeBaseX6, sizeBaseX4,
      fontSizeBodySmall, themeColorTextDark, themeColorIconInverse,
      themeColorBackgroundAccent, themeColorBackgroundDanger,
      themeColorBackgroundWarning, themeColorBackgroundSuccess
    }
  } = getGelTokens();

  const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

  if (!alert?.DisplayAlert) return null;
  if (alert.ShowIfMemberHasIpClaims && !hasIpClaim) return null;
  if (alert.ShowIfMemberHasTpdClaims && !hasTpdClaim) return null;
  if (alert.ShowIfMemberHasTiClaims && !hasTiClaim) return null;

  const alertStyles: Record<AlertTypeEnum, { bg: string; icon: string; color: string }> = {
    [AlertTypeEnum.Info]:    { bg: themeColorBackgroundAccent,  icon: "InfoCircle",   color: themeColorTextDark },
    [AlertTypeEnum.Danger]:  { bg: themeColorBackgroundDanger,  icon: "AlertTriangle", color: themeColorIconInverse },
    [AlertTypeEnum.Success]: { bg: themeColorBackgroundSuccess, icon: "CheckSmall",    color: themeColorTextDark },
    [AlertTypeEnum.Warning]: { bg: themeColorBackgroundWarning, icon: "AlertCircle",   color: themeColorTextDark }
  };

  const style = alertStyles[alert.AlertType];
  if (!style) return null;

  const padding = isXsScreen(screen)
    ? `${sizeBaseX3}px ${sizeBaseX4}px`
    : `${sizeBaseX3}px ${sizeBaseX10}px`;

  return (
    <GelBoxLayout style={{ backgroundColor: style.bg, padding }} space={[0.5, 10]}>
      <GelIcon name={style.icon} width={sizeBaseX6} color={style.color} />
      <GelParagraph style={{ fontSize: fontSizeBodySmall, color: style.color }}>
        {alert.AlertMessage}
      </GelParagraph>
    </GelBoxLayout>
  );
};